import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Box, Chip, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material';
import { NAVIGATION_COMPONENT_TYPE } from 'constant/NavigationConstant';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef } from 'react';
import { MyViewDashboardItem, QuickAccessItem } from 'types/api/Tenant/ConfigurationTypes';

const ACCENT_GREEN = '#42BB93';
const ACCENT_BLUE = '#3f50b5';
const DESCRIPTION_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li'];

// Descriptions are HTML from the My View settings editor.
const sanitizeDescription = (description?: string | null) => {
  if (!description) return '';
  const html = DOMPurify.sanitize(description, { ALLOWED_TAGS: DESCRIPTION_TAGS, ALLOWED_ATTR: [] });
  // A description with no text left can still arrive as bare tags such as <p></p>.
  return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ? html : '';
};

const appendMemberScope = (description: string, memberName?: string) => {
  const normalizedMemberName = memberName?.trim();
  return normalizedMemberName ? `${description} Focus on ${normalizedMemberName}.` : description;
};

export const getMyViewItemDescription = (item: QuickAccessItem): string => {
  const secondaryTitle = item.secondaryTitle?.trim();
  let description: string;

  switch (item.componentType) {
    case NAVIGATION_COMPONENT_TYPE.INSIGHT_VIEW:
      description = secondaryTitle
        ? `Review ${item.title} insights from ${secondaryTitle}.`
        : `Review the latest insights and trends in ${item.title}.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.CHART:
      description = `Explore the charts and visual trends in ${item.title}.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.STANDARD_VIEW:
      description = secondaryTitle
        ? `Review ${item.title} information from ${secondaryTitle}.`
        : `Review the information available in ${item.title}.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.CUBE:
      description = `Explore ${item.title} data across its available dimensions.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.EVENT:
      description = secondaryTitle
        ? `Review and manage ${item.title} events for ${secondaryTitle}.`
        : `Review and manage ${item.title} events.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.SUBJECT:
      description = `Enter or review ${item.title} information.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.SUBJECT_PROCEDURE:
      description = `Continue the ${item.title} process.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.TABLE_ANALYTIC:
      description = `Analyse ${item.title} in a structured table.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.K_TREE:
      description = secondaryTitle
        ? `Explore the ${item.title} hierarchy for ${secondaryTitle}.`
        : `Explore the ${item.title} hierarchy and its related records.`;
      break;
    case NAVIGATION_COMPONENT_TYPE.DASHBOARD:
      description = `Open the ${item.title} dashboard and review its latest information.`;
      break;
    default:
      description = `Open ${item.title} and continue your work.`;
  }

  return appendMemberScope(description, item.memberName);
};

interface MyViewDashboardPreviewProps {
  dashboard: MyViewDashboardItem;
  onItemClick: (item: QuickAccessItem) => void;
  onItemPrefetch?: (item: QuickAccessItem, nextItem?: QuickAccessItem) => void;
}

export function MyViewDashboardPreview({
  dashboard,
  onItemClick,
  onItemPrefetch,
}: Readonly<MyViewDashboardPreviewProps>) {
  const itemCount = dashboard.myViews.length;
  const descriptionHtml = useMemo(() => sanitizeDescription(dashboard.description), [dashboard.description]);
  const previewRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previewRef.current?.focus();
  }, [dashboard.id]);

  useEffect(() => {
    const firstItem = dashboard.myViews[0];
    if (!firstItem) return;

    const handleEnter = (event: KeyboardEvent) => {
      if (
        event.key !== 'Enter' ||
        event.defaultPrevented ||
        event.repeat ||
        event.isComposing ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          Boolean(target.closest('button, a, input, textarea, select, [role="button"], [role="link"]')))
      ) {
        return;
      }

      event.preventDefault();
      onItemPrefetch?.(firstItem, dashboard.myViews[1]);
      onItemClick(firstItem);
    };

    window.addEventListener('keydown', handleEnter);
    return () => window.removeEventListener('keydown', handleEnter);
  }, [dashboard.myViews, onItemClick, onItemPrefetch]);

  return (
    <Box
      ref={previewRef}
      component='section'
      tabIndex={-1}
      aria-label={`${dashboard.dashboardName} My View items`}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: 320,
        border: '1px solid #e2e6ec',
        borderRadius: '8px',
        backgroundColor: '#fff',
        p: { xs: 2, lg: 3 },
        '&:focus': { outline: 'none' },
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={1.5} mb={2.5}>
        <Stack minWidth={0}>
          {descriptionHtml ? (
            <Typography
              component='div'
              variant='body2'
              sx={{
                color: '#667085',
                mt: 0.25,
                '& p': { m: 0 },
                '& p + p': { mt: 0.75 },
                '& ul, & ol': { my: 0.5, pl: 2.5 },
              }}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          ) : (
            <>
              <Typography variant='body2' sx={{ color: '#667085', mt: 0.25 }}>
                {itemCount === 1
                  ? `The ${dashboard.dashboardName} process involves 1 step:`
                  : `The ${dashboard.dashboardName} process involves ${itemCount} steps:`}
              </Typography>
              {itemCount > 0 && (
                <Typography variant='caption' sx={{ color: '#68758a', mt: 0.5 }}>
                  You can select them in any order by clicking on a step, or just press Enter now to continue.
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Chip
          label={`${itemCount} ${itemCount === 1 ? 'step' : 'steps'}`}
          size='small'
          variant='outlined'
          sx={{
            alignSelf: { xs: 'flex-start', sm: 'center' },
            minWidth: 84,
            borderColor: '#d8def7',
            backgroundColor: '#f8f9ff',
            color: ACCENT_BLUE,
            fontWeight: 700,
          }}
        />
      </Stack>

      {itemCount === 0 ? (
        <Stack alignItems='center' justifyContent='center' minHeight={180} gap={0.5} textAlign='center'>
          <Typography fontWeight={700} color='#344054'>
            No My View items configured
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Add items to this dashboard before opening it from Home.
          </Typography>
        </Stack>
      ) : (
        <List disablePadding aria-label={`${dashboard.dashboardName} items`}>
          {dashboard.myViews.map((item, index) => {
            return (
              <ListItem disablePadding key={item.viewId || item.value} sx={{ mb: 1.25 }}>
                <ListItemButton
                  onClick={() => onItemClick(item)}
                  onMouseEnter={() => onItemPrefetch?.(item, dashboard.myViews[index + 1])}
                  sx={{
                    minHeight: 78,
                    gap: 2,
                    border: '1px solid #e3e7ed',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
                    '&:hover, &:focus-visible': {
                      borderColor: ACCENT_GREEN,
                      backgroundColor: '#f1fbf8',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${ACCENT_BLUE}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <Box
                    aria-hidden='true'
                    sx={{
                      width: 46,
                      height: 46,
                      flexShrink: 0,
                      borderRadius: '12px',
                      backgroundColor: '#f2f4f7',
                      color: '#667085',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 800,
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </Box>
                  <Stack minWidth={0} flex={1}>
                    <Typography
                      sx={{
                        color: '#263142',
                        fontSize: 15,
                        fontWeight: 800,
                        lineHeight: 1.35,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Stack direction='row' alignItems='center' gap={1} mt={0.35} flexWrap='wrap'>
                      <Typography variant='body2' sx={{ color: '#667085', lineHeight: 1.4 }}>
                        {getMyViewItemDescription(item)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <KeyboardArrowRight sx={{ flexShrink: 0, color: ACCENT_BLUE }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

    </Box>
  );
}
